import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faEdit,
  faBan,
  faUndo,
  faChevronLeft,
  faChevronRight,
  faEye,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';

const CertificatesList = () => {
  const navigate = useNavigate();
  const [certs, setCerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Filters
  const [search, setSearch] = useState('');
  const [tierId, setTierId] = useState('');
  const [revoked, setRevoked] = useState('');

  // Modals
  const [editModalCert, setEditModalCert] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [revokeModalCert, setRevokeModalCert] = useState(null);
  const [revokeNote, setRevokeNote] = useState('');
  const [savingRevoke, setSavingRevoke] = useState(false);

  const fetchCerts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await streetlearnApi.getCertificates({
        page,
        limit,
        q: search.trim() || undefined,
        tier_id: tierId ? Number(tierId) : undefined,
        revoked: revoked !== '' ? revoked : undefined,
      });
      setCerts(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, tierId, revoked]);

  useEffect(() => {
    fetchCerts();
  }, [fetchCerts]);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      showToast.error('Name cannot be empty');
      return;
    }
    try {
      setSavingEdit(true);
      await streetlearnApi.updateCertificate(editModalCert.id, { name_on_cert: editName.trim() });
      showToast.success('Certificate name updated successfully');
      setEditModalCert(null);
      fetchCerts();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to update certificate name');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRevoke = async (e) => {
    e.preventDefault();
    if (!revokeNote.trim()) {
      showToast.error('Please enter a note for revocation');
      return;
    }
    try {
      setSavingRevoke(true);
      await streetlearnApi.revokeCertificate(revokeModalCert.id, { note: revokeNote.trim() });
      showToast.success('Certificate revoked successfully');
      setRevokeModalCert(null);
      setRevokeNote('');
      fetchCerts();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to revoke certificate');
    } finally {
      setSavingRevoke(false);
    }
  };

  const handleRestore = async (cert) => {
    if (!window.confirm(`Restore certificate ${cert.serial}?`)) return;
    try {
      await streetlearnApi.restoreCertificate(cert.id);
      showToast.success('Certificate restored to valid status');
      fetchCerts();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to restore certificate');
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="mt-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faAward} className="me-2 text-primary" />
            StreetLearn Tier Certificates
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-default btn-sm mx-1" onClick={fetchCerts} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>

          {total > 0 && (
            <div className="d-inline-flex align-items-center ms-2">
              <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Table Panel (Exact Subscriber Module UI) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-0">
          <div className="table-responsive">
            <table className="table table-condensed table-hover bg-white sticky-table m-0">
              <thead>
                <tr>
                  <th style={{ minWidth: '170px' }}>Serial Number</th>
                  <th>Name on Certificate</th>
                  <th>Learner Details</th>
                  <th>Tier & Program</th>
                  <th style={{ minWidth: '140px' }}>Issued Date</th>
                  <th style={{ minWidth: '120px' }}>Status</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                </tr>
                {/* In-Table Filter Row */}
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </th>
                  <th></th>
                  <th></th>
                  <th>
                    <select
                      value={tierId}
                      onChange={(e) => {
                        setTierId(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="1">Tier 1: Foundation</option>
                      <option value="2">Tier 2: Intermediate</option>
                      <option value="3">Tier 3: Advanced</option>
                      <option value="4">Tier 4: Master</option>
                    </select>
                  </th>
                  <th></th>
                  <th>
                    <select
                      value={revoked}
                      onChange={(e) => {
                        setRevoked(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="0">Valid</option>
                      <option value="1">Revoked</option>
                    </select>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear all filters"
                      onClick={() => {
                        setSearch('');
                        setTierId('');
                        setRevoked('');
                        setPage(1);
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <FontAwesomeIcon icon={faRefresh} spin className="me-2 text-primary" />
                      Loading certificates...
                    </td>
                  </tr>
                ) : certs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      No certificates found.
                    </td>
                  </tr>
                ) : (
                  certs.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="font-bold text-primary">
                          <FontAwesomeIcon icon={faAward} className="me-1" />
                          <code>{c.serial}</code>
                        </div>
                      </td>
                      <td>
                        <div className="font-bold text-dark">{c.name_on_cert}</div>
                      </td>
                      <td>
                        <div className="font-bold">{c.learner_phone}</div>
                        {c.learner_name && <div className="text-muted small">{c.learner_name}</div>}
                      </td>
                      <td>
                        <span className="font-weight-bold text-primary">
                          Tier {c.tier_id}: {c.tier_name}
                        </span>
                        <div className="text-muted small">{c.certificate_title}</div>
                      </td>
                      <td className="small text-muted">
                        {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        {!c.revoked_at ? (
                          <span className="text-success font-weight-bold">Valid</span>
                        ) : (
                          <div>
                            <span className="text-danger font-weight-bold">Revoked</span>
                            <div className="text-muted small">{c.revoke_note}</div>
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-default btn-xs mx-1"
                          title="View Learner Profile"
                          onClick={() => navigate(`/streetlearn/learners/${c.learner_id}`)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          className="btn btn-default btn-xs mx-1"
                          title="Edit Name on Certificate"
                          onClick={() => {
                            setEditModalCert(c);
                            setEditName(c.name_on_cert);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        {!c.revoked_at ? (
                          <button
                            className="btn btn-danger btn-xs mx-1"
                            title="Revoke Certificate"
                            onClick={() => {
                              setRevokeModalCert(c);
                              setRevokeNote('');
                            }}
                          >
                            <FontAwesomeIcon icon={faBan} />
                          </button>
                        ) : (
                          <button
                            className="btn btn-success btn-xs mx-1"
                            title="Restore Certificate"
                            onClick={() => handleRestore(c)}
                          >
                            <FontAwesomeIcon icon={faUndo} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Name Modal */}
      {editModalCert && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSaveName}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">Edit Certificate Name</h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setEditModalCert(null)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    Update the student name printed on certificate serial{' '}
                    <strong>{editModalCert.serial}</strong>.
                  </p>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      Name on Certificate <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setEditModalCert(null)}
                    disabled={savingEdit}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={savingEdit}>
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {revokeModalCert && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleRevoke}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">Revoke Certificate</h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setRevokeModalCert(null)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    Are you sure you want to revoke certificate{' '}
                    <strong>{revokeModalCert.serial}</strong> issued to{' '}
                    <strong>{revokeModalCert.name_on_cert}</strong>?
                  </p>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      Revocation Reason / Note <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="e.g. Exam malpractice / Chargeback / Name correction reissue"
                      value={revokeNote}
                      onChange={(e) => setRevokeNote(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setRevokeModalCert(null)}
                    disabled={savingRevoke}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger btn-sm" disabled={savingRevoke}>
                    {savingRevoke ? 'Revoking...' : 'Confirm Revoke'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificatesList;
